import React, { lazy, Suspense, useMemo } from "react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import { useAuth } from "../Provider/AuthProvider";
import { useAppSelector } from "../store/hooks";
import { getPermissionsFromEntitlement } from "../constants/permissionKeys";
import {
  PERMISSION_TO_ROUTE,
  PERMISSION_TO_COMPONENT,
} from "../utils/permissionMappings";

import Loader from "../Loader";
import { NotificationProvider } from "../components/notifications/NotificationProvider";
import TermsOfUse from "../components/Terms/TermsOfUse";

const LoginPage = lazy(() => import("../components/login/LoginPage"));
const TicketDetail = lazy(
  () => import("../components/TicketManagement/TicketDetail"),
);
const Dashboard = lazy(() => import("../components/Dashboard/Dashboard"));
const Logout = lazy(() => import("../utils/Logout"));
const DashboardRedirect = lazy(() => import("../routes/DashboardRedirect"));
const Unauthorized = lazy(() => import("../components/common/Unauthorized"));
const ForgotPasswordOtp = lazy(() => import("../components/auth/ForgotPasswordOtp"));
const ForgotPasswordReset = lazy(() => import("../components/auth/ForgotPasswordReset"));
const BannerFormPage = lazy(() => import("../components/Banner/BannerFormPage"));

const Routes = () => {
  const { token, isAuthenticated, role } = useAuth();
  const entitlement = useAppSelector((state) => state.userEntitlement?.data);
  const entitlementLoading = useAppSelector(
    (state) => state.userEntitlement?.loading,
  );
  const authInitializing = Boolean(token) && !role;
  const userId = entitlement?.id ?? entitlement?.userId ?? null;

  // Get dashboard path based on role
  const getDashboardPath = (userRole) => {
    const roleToPath = {
      USER: "/user-dashboard",
      APPROVER: "/approver-dashboard",
      ADMIN: "/admin-dashboard",
      SUPER_ADMIN: "/superadmin-dashboard",
      SUPPORT_ADMIN: "/supportadmin-dashboard",
      CONSULTANT: "/consultant-dashboard",
    };
    return roleToPath[userRole] || "/user-dashboard";
  };

  // Build routes dynamically from entitlement
  const buildDashboardRoutes = useMemo(() => {
    if (!entitlement || !role) {
      return [];
    }

    const permissions = getPermissionsFromEntitlement(entitlement);
    const routes = [];
    const routePaths = new Set();

    // Build routes from permissions
    permissions.forEach((permissionKey) => {
      const routePath = PERMISSION_TO_ROUTE[permissionKey];
      const Component = PERMISSION_TO_COMPONENT[permissionKey];

      if (routePath && Component && !routePaths.has(routePath)) {
        // Handle routes with ticket detail children
        if (
          routePath === "all-tickets" ||
          routePath === "approvetickets" ||
          routePath === "my-tickets" ||
          routePath === "my-assigned-tickets"
        ) {
          routes.push({
            path: routePath,
            children: [
              { path: "", element: <Component /> },
              { path: ":ticketId", element: <TicketDetail /> },
            ],
          });
        } else if (routePath === "company-register") {
          routes.push({
            path: routePath,
            children: [
              { path: "", element: <Component /> },
              { path: ":id", element: <Component /> },
            ],
          });
        } else if (routePath === "user-registration") {
          routes.push({
            path: routePath,
            children: [
              { path: "", element: <Component /> },
              { path: "view/:id", element: <Component /> },
              // keep edit route compatible when navigated as child
              { path: "edit/:id", element: <Component /> },
            ],
          });
        } else if (routePath === "baner-management") {
          routes.push({
            path: routePath,
            children: [
              { path: "", element: <Component /> },
              { path: "create", element: <BannerFormPage /> },
              { path: "edit/:id", element: <BannerFormPage /> },
            ],
          });
        } else if (routePath === "change-requests") {
          routes.push({
            path: routePath,
            children: [
              { path: "", element: <Component /> },
              { path: "create", element: <Component mode="create" /> },
              { path: "view/:id", element: <Component mode="view" /> },
              { path: "edit/:id", element: <Component mode="edit" /> },
            ],
          });
        } else {
          // Simple routes (including nested routes)
          routes.push({
            path: routePath,
            element: <Component />,
          });
        }
        routePaths.add(routePath);
      }
    });

    return routes;
  }, [entitlement, role]);

  const routesForPublic = [
    { path: "/", element: <LoginPage /> },
    { path: "/login", element: <LoginPage /> },
    { path: "/auth/forgot-password/otp", element: <ForgotPasswordOtp /> },
    { path: "/auth/forgot-password/reset", element: <ForgotPasswordReset /> },
    { path: "/logout", element: <Logout /> },
    { path: "/terms-of-use", element: <TermsOfUse /> },
    { path: "/unauthorized", element: <Unauthorized /> },
  ];

  // Build dashboard routes dynamically
  const routesForAuthenticatedOnly = useMemo(() => {
    if (!role) return [];

    const dashboardPath = getDashboardPath(role);
    const dashboardChildren = entitlementLoading
      ? [{ path: "*", element: <Loader /> }]
      : buildDashboardRoutes.length > 0
        ? [
            { path: "", element: <DashboardRedirect /> },
            ...buildDashboardRoutes,
            { path: "*", element: <Navigate to="/unauthorized" replace /> },
          ]
        : [{ path: "*", element: <Navigate to="/unauthorized" replace /> }];

    return [
      {
        path: "/dashboard",
        element: <Navigate to={dashboardPath} replace />,
      },
      {
        path: dashboardPath,
        element: <Dashboard />,
        children: dashboardChildren,
      },
    ];
  }, [role, buildDashboardRoutes, entitlementLoading]);

  const router = createBrowserRouter([
    ...routesForPublic,
    ...routesForAuthenticatedOnly,
    {
      path: "*",
      element: authInitializing ? (
        <Loader />
      ) : isAuthenticated ? (
        <Navigate to="/unauthorized" replace />
      ) : (
        <Navigate to="/" replace />
      ),
    },
  ]);

  if (authInitializing) {
    return (
      <NotificationProvider userId={userId}>
        <Loader />
      </NotificationProvider>
    );
  }

  return (
    <NotificationProvider userId={userId}>
      <Suspense fallback={<Loader />}>
        <RouterProvider router={router} />
      </Suspense>
    </NotificationProvider>
  );
};

export default Routes;
